/**
 * Open a scene from the cloud library.
 *
 * Downloads the .pr file from R2, loads it into the viewport via the
 * existing scene loading pipeline, and tracks the library item ID so
 * subsequent saves update the same item.
 *
 * Blend files referenced by the scene are resolved by content hash —
 * the scene's .pr blendFileRef stores the SHA-256, and we look up the
 * corresponding blend library item to get a download URL.  If the blend
 * asset has been deleted, the scene degrades gracefully (no blend data).
 */

import type { LibraryItemResponse } from "@poseref/shared";
import { findItemByHash } from "../api/upload.ts";
import { parseBlendFileAsync } from "../blend-reader/parseBlendFileAsync.ts";
import { DriverGraph } from "../scene-graph/DriverGraph.ts";
import { showInvalidSceneObjectsDialog } from "../ui/InvalidSceneObjectsDialog.tsx";
import { clearModifierCache } from "../viewport/modifierApply.ts";
import { setActivePage } from "./activePage.ts";
import {
  allShapeKeySets,
  ensureShapeKeyWeights,
  findEntryByHash,
  generateBlendFileId,
  initBlendVisibility,
} from "./blendFileQueries.ts";
import { computeFileHash } from "./fileHash.ts";
import { setFileHandle } from "./fileSystemAccess.ts";
import { applySceneDocument } from "./persistence/sceneDocument.ts";
import { parseSceneDocument } from "./persistence/sceneDocumentIO.ts";
import type {
  RawSceneDocument,
  RawSessionDocument,
} from "./persistence/types.ts";
import { clearSessionState, loadSessionState } from "./persistence.ts";
import {
  setCurrentLibraryItemId,
  setPendingFolderId,
} from "./saveToLibrary.ts";
import { useStore } from "./store.ts";
import { suppressDirtyTracking } from "./storeDirtyTracking.ts";
import type { AppState, BlendFileEntry } from "./storeTypes.ts";
import { IDENTITY_GROUP_TRANSFORM } from "./storeTypes.ts";
import { undoManager } from "./storeUndo.ts";

/**
 * Open a library scene in the viewport.
 */
export async function openSceneFromLibrary(
  item: LibraryItemResponse,
): Promise<void> {
  if (!item.fileUrl) {
    throw new Error("Library item has no file URL");
  }

  try {
    // 0. Snapshot the local session cache *before* loading. We'll use it
    //    in step 2 to decide whether a newer local autosave should be
    //    loaded in place of the cloud version; once the new scene is
    //    applied, the autosave debounce would overwrite the session.
    const { session: previousSession } = await loadSessionState();

    useStore.setState({ loading: true, error: null });

    // 1. Download the .pr file from R2 (no-store to bypass browser cache)
    const res = await fetch(item.fileUrl, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to download scene: ${res.statusText}`);
    const text = await res.text();
    const cloudData = await parseSceneDocument(text);

    // 2. Pick the effective scene document. A local autosave that belongs
    //    to THIS library item AND is newer than the cloud's modified time
    //    represents unsaved work from a crashed or closed session — load
    //    it seamlessly and flag the scene dirty so the user can save it
    //    back. Stale autosaves (older than cloud, or for a different
    //    scene) are discarded here — there is no more dialog prompt.
    const recovered = pickLocalRecovery(previousSession, item);
    // Accept either envelope: `applySceneDocument` and the blend-resolve
    // path both work off the shared `SerializedSceneBody` fields, so a
    // recovered session document is a drop-in replacement for the cloud
    // scene. Keeping the union typed avoids a lossy narrowing cast.
    const sceneData: RawSceneDocument | RawSessionDocument =
      recovered ?? cloudData;
    if (!recovered && previousSession) {
      clearSessionState().catch(() => {});
    }

    // 3. Apply
    const { patch, omittedObjects } = applySceneDocument(
      sceneData,
      useStore.getState(),
    );
    if (omittedObjects.length > 0) {
      const cleanName = item.name.replace(/\.pr$/i, "");
      const result = await showInvalidSceneObjectsDialog(
        cleanName,
        omittedObjects,
      );
      if (result === "back") {
        useStore.setState({ loading: false });
        setActivePage("library");
        return;
      }
    }

    // 4. Reset undo state for fresh session
    undoManager.clear();
    setFileHandle(null);
    patch.canUndo = false;
    patch.canRedo = false;
    // Dirty flag reflects the load source: the cloud copy is clean; a
    // recovered local autosave is unsaved work by definition.
    patch.sceneDirty = recovered != null;

    // Use the library item name (without .pr) as the scene file name
    const cleanName = item.name.replace(/\.pr$/i, "");
    patch.currentSceneFileName = cleanName + ".pr";

    // 5. Resolve blend file references by content hash.
    //    The scene's .pr stores blendFileRefs (one per loaded .blend).
    //    We resolve each independently — some may already be in memory,
    //    others need downloading from the library.
    const currentState = useStore.getState();
    const refs = sceneData.blendFileRefs;

    if (refs.length > 0) {
      const newFiles = new Map<string, BlendFileEntry>();

      for (const ref of refs) {
        const matchingEntry = findEntryByHash(
          currentState.blendFiles,
          ref.sha256,
        );
        if (matchingEntry) {
          // Matching blend already in memory — restore group transform
          const [entryId, entry] = matchingEntry;
          const savedGt = sceneData.groupTransforms[entryId];
          const groupTransform = savedGt
            ? {
                position: [...savedGt.position] as [number, number, number],
                rotation: [...savedGt.rotation] as [
                  number,
                  number,
                  number,
                  number,
                ],
                scale: [...savedGt.scale] as [number, number, number],
              }
            : { ...IDENTITY_GROUP_TRANSFORM };
          newFiles.set(entryId, { ...entry, groupTransform });
          initBlendVisibility(
            entry.file.objects,
            patch.visibility as Map<string, boolean>,
          );
        } else {
          // Look up the blend asset by hash and download
          const blendAsset = await findItemByHash(ref.sha256);
          if (blendAsset.exists && blendAsset.fileUrl) {
            const entry = await fetchAndParseBlend(
              blendAsset.fileUrl,
              ref,
              sceneData,
              patch,
            );
            if (entry) {
              newFiles.set(entry.id, entry);
            }
          }
          // else: blend asset was deleted — skip gracefully
        }
      }

      if (newFiles.size > 0) {
        patch.blendFiles = newFiles;

        // Reconcile saved weights with actual shape key sets — the .pr file
        // omits all-zero sets for compactness.
        ensureShapeKeyWeights(
          newFiles,
          patch.shapeKeyWeights as Map<string, Map<string, number>>,
        );

        // Rebuild driver graph from ALL entries' shape key sets
        const mergedSKSets = allShapeKeySets(newFiles);
        if (mergedSKSets.size > 0) {
          const dg = new DriverGraph();
          dg.build(mergedSKSets);
          if (patch.shapeKeyWeights) {
            dg.evaluate(
              patch.shapeKeyWeights as Map<string, Map<string, number>>,
            );
          }
          patch.driverGraph = dg;
        } else if (currentState.driverGraph && patch.shapeKeyWeights) {
          currentState.driverGraph.evaluate(
            patch.shapeKeyWeights as Map<string, Map<string, number>>,
          );
        }
      } else {
        clearBlendState(patch);
      }
    } else {
      clearBlendState(patch);
    }

    // 6. Apply the state — keep loading=true so the overlay stays visible
    //    through the page transition and Three.js canvas initialisation.
    //    Invalidate the modifier geometry cache — R3F auto-disposes GPU
    //    resources when the viewport unmounts, so any cached modifier
    //    results from the previous scene are stale.
    //
    //    When we recovered from a local autosave, `patch.sceneDirty` is
    //    set to `true`, but the default dirty-tracking suppression path
    //    applies regardless — the field is written directly and does not
    //    need to be inferred from a Map-identity change.
    clearModifierCache();
    suppressDirtyTracking(true);
    useStore.setState(patch as Partial<AppState>);
    suppressDirtyTracking(false);

    // If the viewport is already mounted (e.g. page refresh restoring the
    // active scene), push the loaded camera pose to the live controls.
    // When the viewport is not yet mounted this is a no-op, and CameraRig's
    // init effect will read the correct position from the store at mount time.
    const next = useStore.getState();
    window.__poseref_cameraController?.applyState({
      projection: next.cameraProjection,
      position: next.cameraPosition,
      target: next.cameraTarget,
      up: next.cameraUp,
      focalLength: next.focalLength,
      orthographicHalfHeight: next.orthographicHalfHeight,
    });

    // Resolve reference image URLs from R2 keys
    useStore.getState().refreshReferenceImageUrls();

    // 7. Track library item for save-back
    setCurrentLibraryItemId(item.id);
    setPendingFolderId(null);

    // 8. Navigate to viewport — loading stays true until the Three.js
    //    canvas fires onCreated, which sets loading=false.
    setActivePage("viewport");
  } catch (e) {
    useStore.setState({
      error: e instanceof Error ? e.message : String(e),
      loading: false,
    });
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Download a .blend from R2, parse it, and return a BlendFileEntry.
 * Also initialises visibility for the new objects in `patch.visibility`.
 *
 * The group transform is restored by looking up the SAVED entry ID from
 * `sceneData.blendFileRefs` (matched by hash), so the key survives the
 * round-trip even though a new runtime entry ID is generated.
 */
async function fetchAndParseBlend(
  blendUrl: string,
  ref: { id: string; fileName: string; sha256: string; objectNames: string[] },
  sceneData: RawSceneDocument | RawSessionDocument,
  patch: Partial<AppState>,
): Promise<BlendFileEntry | null> {
  const blendRes = await fetch(blendUrl);
  if (!blendRes.ok) return null;

  const buffer = await blendRes.arrayBuffer();
  // Keep a copy — parseBlendFileAsync transfers the original to the worker.
  const bufferCopy = buffer.slice(0);
  const [blendFile, hash] = await Promise.all([
    parseBlendFileAsync(buffer),
    computeFileHash(bufferCopy),
  ]);

  // Initialize visibility for blend objects — respect Blender's flags
  initBlendVisibility(
    blendFile.objects,
    patch.visibility as Map<string, boolean>,
  );

  // Use the saved entry ID for group transform lookup.  The .pr file
  // stores groupTransforms keyed by the entry ID that was active at save
  // time.  If we generate a fresh ID, the lookup always misses.
  const savedEntryId = ref.id || "";
  const entryId = generateBlendFileId();

  // Try the saved ID first, then fall back to the new ID (which won't
  // match — but keeps the pattern explicit).
  const savedGt =
    sceneData.groupTransforms[savedEntryId] ??
    sceneData.groupTransforms[entryId];
  const groupTransform = savedGt
    ? {
        position: [...savedGt.position] as [number, number, number],
        rotation: [...savedGt.rotation] as [number, number, number, number],
        scale: [...savedGt.scale] as [number, number, number],
      }
    : { ...IDENTITY_GROUP_TRANSFORM };

  return {
    id: entryId,
    file: blendFile,
    fileName: ref.fileName,
    hash,
    originalBuffer: bufferCopy,
    groupTransform,
  };
}

/** Clear all blend-related state. */
function clearBlendState(patch: Partial<AppState>): void {
  patch.blendFiles = new Map();
  patch.driverGraph = null;
}

/**
 * Decide whether the local autosave should be used in place of the cloud
 * version. Returns the session document when it represents unsaved work
 * newer than the cloud scene, or null when no recovery applies (stale,
 * missing, or for a different library item).
 *
 * Matching by `libraryItemId` — set when a scene is opened from the
 * library — is the authoritative identity check. Older sessions captured
 * before that field existed are treated as stale and discarded, since we
 * can't prove they belong to this scene.
 */
function pickLocalRecovery(
  previousSession: RawSessionDocument | null,
  item: LibraryItemResponse,
): RawSessionDocument | null {
  if (!previousSession || !previousSession.sceneDirty) return null;
  if (previousSession.libraryItemId !== item.id) return null;

  const sessionModifiedAt = previousSession.modifiedAt;
  const cloudUpdatedAt = new Date(item.updatedAt).getTime();
  if (!sessionModifiedAt || sessionModifiedAt <= cloudUpdatedAt) return null;

  return previousSession;
}
